/* eslint-disable camelcase */
import { acceptHMRUpdate, defineStore } from 'pinia'

import { useMiscDraftProgressivePromosStore } from './miscDraftProgressivePromos'

import { PromoKind } from '~/enums/Product'
import { PRODUCT_KINDS } from '~/types/Product'

import type { ProductKind, ProgressivePromo } from '~/types/Product'

const initialState = () => ({
  id: 0,
  code: '',
  kind: '',
  is_percentage: false,
  auto_apply: false,
  actions: PRODUCT_KINDS.reduce(
    (acc, kind) => ({
      ...acc,
      [kind]: {
        value: 0,
        is_percentage: true,
      },
    }),
    {} as Record<ProductKind, { value: number; is_percentage: boolean }>,
  ),
  rule: '',
  progressive_parameters: undefined as
    | undefined
    | ProgressivePromo['progressive_parameters'],
})

const state = initialState()
export type CartPromoState = typeof state

export const useCartPromoStore = defineStore('cartPromo', () => {
  const { $pinia } = useNuxtApp()
  const miscDraftProgressivePromosStore =
    useMiscDraftProgressivePromosStore($pinia)

  const id = ref(state.id)
  const code = ref(state.code)
  const kind = ref(state.kind)
  const is_percentage = ref(state.is_percentage)
  const auto_apply = ref(state.auto_apply)
  const actions = ref(state.actions)
  const progressive_parameters = ref(state.progressive_parameters)
  const rule = ref(state.rule)

  const $state = computed(() => ({
    id: id.value,
    code: code.value,
    kind: kind.value,
    is_percentage: is_percentage.value,
    auto_apply: auto_apply.value,
    actions: actions.value,
    progressive_parameters: progressive_parameters.value,
    rule: rule.value,
  }))

  const IS_PROGRESSIVE_PROMO = computed(() => {
    return miscDraftProgressivePromosStore.list
      .map((promo) => promo.code)
      .includes(code.value)
  })

  const IS_GROOVER_PLUS_PROMO_APPLIED = computed(() => {
    return kind.value === PromoKind.GROOVER_PLUS
  })

  function getStateRef(key: keyof CartPromoState) {
    switch (key) {
      case 'id':
        return id
      case 'code':
        return code
      case 'kind':
        return kind
      case 'is_percentage':
        return is_percentage
      case 'auto_apply':
        return auto_apply
      case 'actions':
        return actions
      case 'progressive_parameters':
        return progressive_parameters
      case 'rule':
        return rule
    }
  }

  function SET_CART_PROMO(patch: Partial<Record<keyof CartPromoState, any>>) {
    Object.keys(patch).forEach((key) => {
      const stateRef = getStateRef(key as keyof CartPromoState)
      if (stateRef === undefined) return

      if (key === 'actions') {
        actions.value = {
          ...initialState().actions,
          ...patch.actions,
        }
        return
      }
      stateRef.value = patch[key as keyof CartPromoState]
    })
  }

  function RESET() {
    SET_CART_PROMO(initialState())
  }

  return {
    // state
    id,
    code,
    kind,
    is_percentage,
    auto_apply,
    actions,
    progressive_parameters,
    rule,

    // getters
    $state,
    IS_PROGRESSIVE_PROMO,
    IS_GROOVER_PLUS_PROMO_APPLIED,

    // actions
    SET_CART_PROMO,
    RESET,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useCartPromoStore, import.meta.hot))
